import React, { createContext, useContext, useState, useEffect } from 'react';

export type FontSize = 'small' | 'medium' | 'large' | 'xlarge';
export type BackgroundMode = 'dark' | 'light' | 'cream' | 'beige' | 'blue-tint';

interface AccessibilitySettings {
  dyslexicMode: boolean;
  fontSize: FontSize;
  backgroundMode: BackgroundMode;
  highContrast: boolean;
  wordSpacing: boolean;
}

interface AccessibilityContextType extends AccessibilitySettings {
  toggleDyslexicMode: () => void;
  setFontSize: (size: FontSize) => void;
  setBackgroundMode: (mode: BackgroundMode) => void;
  toggleHighContrast: () => void;
  toggleWordSpacing: () => void;
  resetSettings: () => void;
}

const defaultSettings: AccessibilitySettings = {
  dyslexicMode: false,
  fontSize: 'medium',
  backgroundMode: 'dark',
  highContrast: false,
  wordSpacing: false,
};

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(() => {
    const saved = localStorage.getItem('accessibility-settings');
    return saved ? JSON.parse(saved) : defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem('accessibility-settings', JSON.stringify(settings));
    
    // Apply classes to body
    document.body.classList.toggle('dyslexic-mode', settings.dyslexicMode);
    document.body.classList.toggle('high-contrast', settings.highContrast);
    
    // Remove all font size classes
    document.body.classList.remove('font-size-small', 'font-size-medium', 'font-size-large', 'font-size-xlarge');
    document.body.classList.add(`font-size-${settings.fontSize}`);
    
    // Remove all background mode classes
    document.body.classList.remove('bg-cream', 'bg-beige', 'bg-blue-tint');
    if (settings.backgroundMode !== 'dark' && settings.backgroundMode !== 'light') {
      document.body.classList.add(`bg-${settings.backgroundMode}`);
    }
  }, [settings]);

  const toggleDyslexicMode = () => {
    setSettings(prev => ({ ...prev, dyslexicMode: !prev.dyslexicMode }));
  };

  const setFontSize = (fontSize: FontSize) => {
    setSettings(prev => ({ ...prev, fontSize }));
  };

  const setBackgroundMode = (backgroundMode: BackgroundMode) => {
    setSettings(prev => ({ ...prev, backgroundMode }));
  };

  const toggleHighContrast = () => {
    setSettings(prev => ({ ...prev, highContrast: !prev.highContrast }));
  };

  const toggleWordSpacing = () => {
    setSettings(prev => ({ ...prev, wordSpacing: !prev.wordSpacing }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <AccessibilityContext.Provider
      value={{
        ...settings,
        toggleDyslexicMode,
        setFontSize,
        setBackgroundMode,
        toggleHighContrast,
        toggleWordSpacing,
        resetSettings,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};
